/* =============================================================================
   Älly Scandic — brand motion
   Quiet scroll reveals + sticky-header scrim. Dependency-free, ~1KB.
   Animates only transform/opacity. Respects prefers-reduced-motion.
   ============================================================================= */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- Scroll reveals ----------------------------------------------------- */
  function initReveals(root) {
    var targets = (root || document).querySelectorAll(".ally-reveal:not(.is-visible)");
    if (!targets.length) return;

    if (reduce || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // Stagger siblings that opt in via data-ally-stagger on the parent.
        entry.target.classList.add("is-visible");
        io.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.12 });

    targets.forEach(function (el, i) {
      var group = el.closest("[data-ally-stagger]");
      if (group && !el.style.getPropertyValue("--ally-reveal-delay")) {
        var siblings = Array.prototype.slice.call(
          group.querySelectorAll(".ally-reveal")
        );
        var idx = siblings.indexOf(el);
        if (idx > -1) el.style.setProperty("--ally-reveal-delay", (idx * 90) + "ms");
      }
      io.observe(el);
    });
  }

  /* ---- Sticky-header scrim ------------------------------------------------ */
  /* Adds .ally-scrolled to <html> once the viewport leaves the hero, so a
     transparent-over-hero header can transition to a solid bar. Horizon's
     header reads this hook via the CSS in README (optional enhancement). */
  function initHeaderScrim() {
    var sentinel = document.querySelector("[data-ally-hero-sentinel]");
    var root = document.documentElement;
    if (!sentinel || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      root.classList.toggle("ally-scrolled", !entries[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(sentinel);
  }

  /* ---- Baby footsteps trail ---------------------------------------------- */
  /* A winding S-curve of baby footprints down the page. Anchored to document
     position (moves with the page), revealing as you scroll down and receding
     as you scroll up. Homepage only, fixed low-opacity overlay, no pointer
     capture. Off entirely under reduced-motion. */
  var FOOT_SVG =
    '<svg viewBox="0 0 30 44" fill="currentColor" aria-hidden="true">' +
    '<path d="M15 4C9 4 6 9 6 16c0 7 3 14 9 14s9-7 9-14c0-7-3-12-9-12z"/>' +
    '<circle cx="9" cy="35" r="2.4"/><circle cx="13.4" cy="37.4" r="2.1"/>' +
    '<circle cx="17.6" cy="37.4" r="1.9"/><circle cx="21" cy="35" r="1.7"/>' +
    '<circle cx="23.4" cy="32" r="1.4"/></svg>';

  function initFootprints() {
    if (reduce) return;
    if (!document.querySelector(".ally-hero")) return;        // homepage only
    if (document.querySelector(".ally-trail")) return;

    var layer = document.createElement("div");
    layer.className = "ally-trail";
    layer.setAttribute("aria-hidden", "true");
    var inner = document.createElement("div");
    inner.className = "ally-trail__inner";
    layer.appendChild(inner);
    document.body.appendChild(layer);

    var prints = [];

    function build() {
      inner.innerHTML = "";
      prints = [];
      var docH = document.documentElement.scrollHeight;
      var vw = window.innerWidth;
      var vh = window.innerHeight;
      var startY = vh * 0.9;
      var endY = docH - vh * 0.35;
      var spacing = 108;
      var amp = Math.min(vw * 0.33, 500);   // wider swing (more S)
      var wave = 230;                        // shorter period → full S curves
      var cx = vw * 0.5;
      var i = 0;
      for (var y = startY; y <= endY; y += spacing) {
        var x = cx + amp * Math.sin(y / wave);
        var dx = (amp * Math.cos(y / wave)) / wave;          // path tangent
        var angle = (Math.atan2(dx, 1) * 180) / Math.PI;
        var side = i % 2 === 0 ? 1 : -1;                     // alternate feet
        var el = document.createElement("span");
        el.className = "ally-trail__step";
        el.innerHTML = FOOT_SVG;
        el.style.left = (x + side * 15) + "px";
        el.style.top = y + "px";
        el.style.setProperty("--r", angle.toFixed(1) + "deg");
        el.style.setProperty("--flip", side);
        inner.appendChild(el);
        prints.push({ el: el, y: y, on: false });
        i++;
      }
    }

    var ticking = false;
    function update() {
      var sy = window.pageYOffset || document.documentElement.scrollTop;
      inner.style.transform = "translate3d(0," + (-sy) + "px,0)";
      var threshold = sy + window.innerHeight * 0.72;
      for (var k = 0; k < prints.length; k++) {
        var p = prints[k];
        var should = p.y <= threshold;
        if (should !== p.on) {
          p.on = should;
          p.el.classList.toggle("is-on", should);
        }
      }
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    build();
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    var rt;
    window.addEventListener("resize", function () {
      clearTimeout(rt);
      rt = setTimeout(function () { build(); update(); }, 200);
    });
  }

  /* ---- Image stack (vertical coverflow) ---------------------------------- */
  function initOneStack(vp) {
    if (!vp || vp.dataset.stackBound === "1") return;
    vp.dataset.stackBound = "1";
    var cards = Array.prototype.slice.call(vp.querySelectorAll("[data-stack-card]"));
    var n = cards.length;
    if (!n) return;
    var active = 0;
    var interval = parseInt(vp.dataset.interval, 10) || 3000;
    var timer = null;
    var SCALE_STEP = 0.13, DEPTH = 130;
    var curEl = vp.querySelector("[data-stack-current]");
    var stage = vp.querySelector(".ally-stack__stage");

    function sizeStage() {
      var maxH = 0;
      for (var i = 0; i < n; i++) { var h = cards[i].offsetHeight; if (h > maxH) maxH = h; }
      if (maxH > 80 && stage) stage.style.height = maxH + "px";
    }
    function refresh() { sizeStage(); layout(); }

    function layout() {
      var ch = cards[active] ? cards[active].offsetHeight : 360;
      var GAP = Math.max(56, ch * 0.16);
      for (var i = 0; i < n; i++) {
        var off = i - active;
        if (off > n / 2) off -= n;
        if (off < -n / 2) off += n;
        var ab = Math.abs(off);
        var card = cards[i];
        var scale = Math.max(1 - ab * SCALE_STEP, 0.55);
        var op = ab === 0 ? 1 : ab === 1 ? 0.55 : ab === 2 ? 0.22 : 0;
        card.style.transform =
          "translate(-50%,-50%) translate3d(0," + off * GAP + "px," + (-ab * DEPTH) + "px) scale(" + scale + ")";
        card.style.opacity = op;
        card.style.zIndex = String(100 - ab);
        card.style.pointerEvents = ab === 0 ? "auto" : "none";
        card.classList.toggle("is-active", ab === 0);
      }
      if (curEl) curEl.textContent = String(active + 1);
    }
    function go(d) { active = (active + d + n) % n; layout(); }
    function start() { if (reduce || timer) return; timer = setInterval(function () { go(1); }, interval); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    vp.addEventListener("mouseenter", stop);
    vp.addEventListener("mouseleave", start);
    vp.addEventListener("focusin", stop);
    vp.addEventListener("focusout", start);
    var prev = vp.querySelector("[data-stack-prev]");
    var next = vp.querySelector("[data-stack-next]");
    if (prev) prev.addEventListener("click", function () { go(-1); });
    if (next) next.addEventListener("click", function () { go(1); });
    cards.forEach(function (c, i) {
      c.addEventListener("click", function () { if (i !== active) { active = i; layout(); } });
      var im = c.querySelector("img");
      if (im && !im.complete) im.addEventListener("load", refresh);
    });
    window.addEventListener("load", refresh);
    window.addEventListener("resize", refresh);

    refresh();
    start();
  }
  function initImageStack(root) {
    (root || document).querySelectorAll("[data-ally-stack]").forEach(initOneStack);
  }

  /* ---- Variation size switch (Suur / Väike) ------------------------------ */
  function initVariantSwitch(root) {
    (root || document).querySelectorAll("[data-variant-card]").forEach(function (card) {
      if (card.dataset.variantBound === "1") return;
      card.dataset.variantBound = "1";
      var btns = card.querySelectorAll("[data-variant-btn]");
      var imgs = card.querySelectorAll("[data-variant-img]");
      var priceEl = card.querySelector("[data-variant-price]");
      var links = card.querySelectorAll("[data-variant-link]");
      btns.forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          var id = btn.getAttribute("data-variant-btn");
          btns.forEach(function (b) {
            var on = b === btn;
            b.classList.toggle("is-active", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
          });
          imgs.forEach(function (im) {
            im.classList.toggle("is-active", im.getAttribute("data-variant-img") === id);
          });
          if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
          if (btn.dataset.url) links.forEach(function (a) { a.setAttribute("href", btn.dataset.url); });
        });
      });
    });
  }

  /* ---- Review slider (one column, manual up/down) ------------------------ */
  function initReviewSlider(root) {
    (root || document).querySelectorAll("[data-revw]").forEach(function (el) {
      if (el.dataset.revwBound === "1") return;
      el.dataset.revwBound = "1";
      var cards = el.querySelectorAll("[data-revw-card]");
      var n = cards.length;
      if (!n) return;
      var i = 0;
      var desktop = window.matchMedia("(min-width: 1000px)");
      var prev = el.querySelector("[data-revw-prev]");
      var next = el.querySelector("[data-revw-next]");
      var cur = el.querySelector("[data-revw-current]");
      function pageSize() { return desktop.matches ? 3 : 1; }
      function show(idx) {
        var size = pageSize();
        var pages = Math.ceil(n / size);
        var page = ((Math.floor(idx / size) % pages) + pages) % pages;
        i = page * size;
        cards.forEach(function (c, k) {
          var active = k >= i && k < i + size;
          c.classList.toggle("is-active", active);
          c.setAttribute("aria-hidden", String(!active));
        });
        if (cur) cur.textContent = size > 1 ? String(i + 1) + "–" + String(Math.min(i + size, n)) : String(i + 1);
        window.requestAnimationFrame(updateNavPosition);
      }
      if (prev) prev.addEventListener("click", function () { show(i - pageSize()); });
      if (next) next.addEventListener("click", function () { show(i + pageSize()); });
      var stage = el.querySelector(".ally-revw__stage");
      var mobile = window.matchMedia("(max-width: 749px)");
      var gesture = null;
      function updateNavPosition() {
        if ((!desktop.matches && !mobile.matches) || !stage) return;
        var media = stage.querySelector(".is-active .ally-quote__media img, .is-active .ally-quote__media");
        if (!media) return;
        var imageRect = media.getBoundingClientRect();
        var sliderRect = el.getBoundingClientRect();
        el.style.setProperty("--ally-review-nav-y", String(imageRect.top + imageRect.height / 2 - sliderRect.top) + "px");
      }
      if (stage) {
        if (window.ResizeObserver) {
          var navObserver = new ResizeObserver(function () { updateNavPosition(); });
          navObserver.observe(stage);
        } else {
          window.addEventListener("resize", updateNavPosition);
        }
        stage.querySelectorAll("img").forEach(function (image) { image.addEventListener("load", updateNavPosition); });
      }
      function syncSwipe() {
        gesture = null;
        if (stage) {
          stage.style.touchAction = "pan-y pinch-zoom";
          stage.style.cursor = mobile.matches ? "" : "grab";
        }
      }
      if (stage) {
        stage.addEventListener("pointerdown", function (event) {
          if (!event.isPrimary || event.button !== 0) { gesture = null; return; }
          if (event.target.closest("a, button, input, textarea, select")) return;
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
          if (event.pointerType === "mouse") {
            event.preventDefault();
            stage.style.cursor = "grabbing";
          }
          stage.setPointerCapture(event.pointerId);
        });
        stage.addEventListener("pointermove", function (event) {
          if (!gesture || gesture.id !== event.pointerId) return;
          var dx = event.clientX - gesture.x;
          var dy = event.clientY - gesture.y;
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) syncSwipe();
        });
        stage.addEventListener("pointerup", function (event) {
          if (!gesture || gesture.id !== event.pointerId) return;
          var dx = event.clientX - gesture.x;
          var dy = event.clientY - gesture.y;
          syncSwipe();
          if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.3) show(i + (dx < 0 ? pageSize() : -pageSize()));
        });
        stage.addEventListener("pointercancel", syncSwipe);
        stage.addEventListener("lostpointercapture", syncSwipe);
        stage.addEventListener("dragstart", function (event) { event.preventDefault(); });
        var wheelDistance = 0;
        var wheelTimer;
        var wheelLocked = false;
        stage.addEventListener("wheel", function (event) {
          if (mobile.matches || event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          window.clearTimeout(wheelTimer);
          wheelTimer = window.setTimeout(function () { wheelDistance = 0; wheelLocked = false; }, 180);
          if (wheelLocked) return;
          var distance = event.deltaX * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientWidth : 1);
          if (wheelDistance * distance < 0) wheelDistance = 0;
          wheelDistance += distance;
          if (Math.abs(wheelDistance) >= 48) {
            show(i + (wheelDistance > 0 ? pageSize() : -pageSize()));
            wheelLocked = true;
          }
        }, { passive: false });
        mobile.addEventListener("change", syncSwipe);
        syncSwipe();
      }
      desktop.addEventListener("change", function () { show(i); });
      show(0);
    });
  }

  /* ---- Gallery modal + fullscreen lightbox ------------------------------- */
  function initGalleryModal(root) {
    (root || document).querySelectorAll("[data-gallery-modal]").forEach(function (modal) {
      if (modal.dataset.galleryBound === "1") return;
      modal.dataset.galleryBound = "1";
      var section = modal.closest(".ally-community") || document;
      var openBtn = section.querySelector("[data-gallery-open]");
      var closers = modal.querySelectorAll("[data-gallery-close]");
      var cells = Array.prototype.slice.call(modal.querySelectorAll("[data-gallery-item]"));
      var lbox = modal.querySelector("[data-lightbox]");
      var limg = modal.querySelector("[data-lightbox-img]");
      var lprev = modal.querySelector("[data-lightbox-prev]");
      var lnext = modal.querySelector("[data-lightbox-next]");
      var lclose = modal.querySelector("[data-lightbox-close]");
      var srcs = cells.map(function (c) { return c.getAttribute("data-full"); });
      var li = 0;

      function openModal() {
        modal.hidden = false; modal.setAttribute("aria-hidden", "false");
        document.documentElement.classList.add("ally-no-scroll");
      }
      function closeLbox() { if (lbox) { lbox.hidden = true; lbox.setAttribute("aria-hidden", "true"); } }
      function closeModal() {
        closeLbox();
        modal.hidden = true; modal.setAttribute("aria-hidden", "true");
        document.documentElement.classList.remove("ally-no-scroll");
      }
      function openLbox(idx) {
        if (!srcs.length) return;
        li = (idx + srcs.length) % srcs.length;
        if (limg) limg.src = srcs[li];
        if (lbox) { lbox.hidden = false; lbox.setAttribute("aria-hidden", "false"); }
      }
      function step(d) { openLbox(li + d); }

      if (openBtn) openBtn.addEventListener("click", openModal);
      closers.forEach(function (c) { c.addEventListener("click", closeModal); });
      cells.forEach(function (c, idx) { c.addEventListener("click", function () { openLbox(idx); }); });
      if (lprev) lprev.addEventListener("click", function (e) { e.stopPropagation(); step(-1); });
      if (lnext) lnext.addEventListener("click", function (e) { e.stopPropagation(); step(1); });
      if (lclose) lclose.addEventListener("click", closeLbox);
      if (lbox) lbox.addEventListener("click", function (e) { if (e.target === lbox) closeLbox(); });

      document.addEventListener("keydown", function (e) {
        if (modal.hidden) return;
        if (e.key === "Escape") { if (lbox && !lbox.hidden) closeLbox(); else closeModal(); }
        else if (lbox && !lbox.hidden && e.key === "ArrowRight") step(1);
        else if (lbox && !lbox.hidden && e.key === "ArrowLeft") step(-1);
      });
    });
  }

  function boot() {
    initReveals(document);
    initHeaderScrim();
    initImageStack(document);
    initVariantSwitch(document);
    initReviewSlider(document);
    initGalleryModal(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Re-scan when a merchant adds/edits a section in the Theme Editor. */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (e) {
      initReveals(e.target);
      initImageStack(e.target);
      initVariantSwitch(e.target);
      initReviewSlider(e.target);
      initGalleryModal(e.target);
    });
  }
})();
